import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ParentOrderAlignmentPanel from "../components/ParentOrderAlignmentPanel";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

// ── BANKEX Credentials ─────────────────────────────────────
// Change ACCESS_TOKEN and CLIENT_ID to the account you want to use
const ACCESS_TOKEN = "YOUR_ACCESS_TOKEN_HERE";
const CLIENT_ID    = "YOUR_CLIENT_ID_HERE";

const REFRESH_SEC   = 10;
const UNDERLYING_ID = 69;       // Bankex underlying scrip ID on Dhan
const SEGMENT       = "IDX_I";
const RANGE         = 500;

// ── Formatters ────────────────────────────────────────────────
// Bankex volume → Lakhs (/1e5), same as Nifty
const fmtVolCr = (v) => v != null ? (v / 1e5).toFixed(2) : "—";
const fmtOI    = (v) => v != null ? Number(v).toLocaleString("en-IN") : "0";
const fmtLTP   = (v) => (v != null && Number(v) !== 0) ? Number(v).toFixed(2) : "—";
const fmtTime  = (d) => d ? new Date(d).toLocaleTimeString("en-IN", { hour12: false }) : "";

// ── Styles ────────────────────────────────────────────────────
const C = {
  bg:     "#04090f",
  panel:  "#080f1a",
  card:   "#0a1628",
  border: "#0f2744",
  accent: "#38bdf8",   // cyan for Bankex
  green:  "#22c55e",
  red:    "#ef4444",
  amber:  "#f59e0b",
  text:   "#cbd5e1",
  muted:  "#334155",
  dim:    "#1e3a5f",
};

const S = {
  page:  { minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace", fontSize: 13 },
  card:  { background: C.card, border: `1px solid ${C.border}`, borderRadius: 6 },
  label: { fontSize: 9, color: C.muted, letterSpacing: 3, textTransform: "uppercase", marginBottom: 5 },
  th:    { padding: "10px 14px", textAlign: "right", color: C.muted, fontSize: 9, letterSpacing: 2, fontWeight: "normal", borderBottom: `1px solid ${C.border}` },
  td:    { padding: "9px 14px", textAlign: "right", fontSize: 12, borderBottom: `1px solid #060d18` },
};

const PAGES = [
  { id: "itm",          label: "BANKEX ITM", dot: "#38bdf8" },
  { id: "bankex-oi", label: "OI ANALYSIS",   dot: "#f59e0b" },
];

// ── Fetch helpers ─────────────────────────────────────────────
async function fetchLiveSpot(token, clientId) {
  try {
    const res = await fetch(`${API_BASE}/api/options/live-spot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken: token, clientId, underlyingId: UNDERLYING_ID }),
    });
    const d = await res.json();
    return d.spot || null;
  } catch { return null; }
}

function roundIndexSpotTo100(value) {
  const spot = Number(value || 0);
  if (!spot) return "";
  return String(Math.round(spot / 100) * 100);
}

async function safeFetch(url, body, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (d.error && i < retries) { await new Promise(r => setTimeout(r, 2000)); continue; }
      return d;
    } catch (e) {
      if (i === retries) throw e;
      await new Promise(r => setTimeout(r, 2000));
    }
  }
}

// ── Sentiment Bar ─────────────────────────────────────────────
function SentimentBar({ ceVal, peVal, ceLabel, peLabel, title }) {
  const total = (ceVal || 0) + (peVal || 0);
  if (!total) return null;
  const cePct = ((ceVal / total) * 100).toFixed(1);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", ...S.label, marginBottom: 4 }}>
        <span style={{ color: C.green }}>{ceLabel}</span>
        <span style={{ color: C.dim, fontSize: 8 }}>{title}</span>
        <span style={{ color: C.red }}>{peLabel}</span>
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${cePct}%`, background: `linear-gradient(90deg, ${C.green}, #16a34a)`, transition: "width 0.8s ease" }} />
        <div style={{ flex: 1, background: `linear-gradient(90deg, #b91c1c, ${C.red})` }} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// BANKEX OI ANALYSIS SUB-PAGE
// ════════════════════════════════════════════════════════════════
const OI_REFRESH_SEC = 30;

function BankexOIAnalysis({ state, setState }) {
  const { spotPrice, running, loading, data, error, countdown, view, lastUpdated, fetchingSpot, accessToken, clientId } = state;
  const set = useCallback((k, v) => setState(s => ({ ...s, [k]: v })), [setState]);
  const ivRef = useRef(null), cdRef = useRef(null);

  const [tokenInput,    setTokenInput]    = useState(ACCESS_TOKEN);
  const [clientIdInput, setClientIdInput] = useState(CLIENT_ID);
  const [editingToken,  setEditingToken]  = useState(false);
  const [editingClient, setEditingClient] = useState(false);

  // Load saved token/clientId from localStorage on mount
useEffect(() => {
  const saved = JSON.parse(localStorage.getItem("bx_credentials") || "{}");
  if (saved.token)    { setTokenInput(saved.token);    activeToken.current    = saved.token;    }
  if (saved.clientId) { setClientIdInput(saved.clientId); activeClientId.current = saved.clientId; }
}, []);

  const activeToken    = useRef(ACCESS_TOKEN);
  const activeClientId = useRef(CLIENT_ID);

  useEffect(() => {
    if (accessToken) {
      setTokenInput(accessToken);
      activeToken.current = accessToken;
    }

    if (clientId) {
      setClientIdInput(clientId);
      activeClientId.current = clientId;
    }
  }, [accessToken, clientId]);

  useEffect(() => {
  const saved = JSON.parse(localStorage.getItem("bx_credentials") || "{}");
  if (saved.token)    { setTokenInput(saved.token);       activeToken.current    = saved.token;    }
  if (saved.clientId) { setClientIdInput(saved.clientId); activeClientId.current = saved.clientId; }
}, []);

useEffect(() => {
  if (tokenInput !== ACCESS_TOKEN || clientIdInput !== CLIENT_ID) {
    localStorage.setItem("bx_credentials", JSON.stringify({ token: tokenInput, clientId: clientIdInput }));
  }
}, [tokenInput, clientIdInput]);

  const fetchOI = useCallback(async () => {
    set("loading", true);
    try {
      const expData = await safeFetch(`${API_BASE}/api/options/expiry`, {
        accessToken: activeToken.current, clientId: activeClientId.current,
        underlyingId: UNDERLYING_ID, segment: SEGMENT,
      });
      if (!expData?.expiry) throw new Error("No expiry found");

      const d = await safeFetch(`${API_BASE}/api/options/oi-analysis`, {
        accessToken: activeToken.current, clientId: activeClientId.current,
        expiry: expData.expiry, spotPrice: parseFloat(spotPrice),
        underlyingId: UNDERLYING_ID, segment: SEGMENT,
      });
      if (d?.error) throw new Error(d.error);
      set("data", d);
      set("error", "");
      set("lastUpdated", new Date().toISOString());
      set("countdown", OI_REFRESH_SEC);
    } catch (e) { set("error", e.message?.slice(0, 60)); }
    finally { set("loading", false); }
  }, [spotPrice]);

  const start = async () => {
    activeToken.current    = tokenInput.trim()    || ACCESS_TOKEN;
    activeClientId.current = clientIdInput.trim() || CLIENT_ID;
    localStorage.setItem("bx_credentials", JSON.stringify({ token: activeToken.current, clientId: activeClientId.current }));

    // Persist to localStorage so values survive refresh/page change
localStorage.setItem("bx_credentials", JSON.stringify({
  token:    activeToken.current,
  clientId: activeClientId.current,
}));

    set("running", true); set("error", "");
    await fetchOI();
    if (ivRef.current) clearInterval(ivRef.current);
    if (cdRef.current) clearInterval(cdRef.current);
    ivRef.current = setInterval(fetchOI, OI_REFRESH_SEC * 1000);
    cdRef.current = setInterval(() =>
      setState(s => ({ ...s, countdown: s.countdown <= 1 ? OI_REFRESH_SEC : s.countdown - 1 }))
    , 1000);
  };

  const stop = () => {
    clearInterval(ivRef.current); clearInterval(cdRef.current);
    ivRef.current = null; cdRef.current = null;
    set("running", false); set("countdown", OI_REFRESH_SEC);
  };

  useEffect(() => {
    if (state.running && !ivRef.current) {
      ivRef.current = setInterval(fetchOI, OI_REFRESH_SEC * 1000);
      cdRef.current = setInterval(() =>
        setState(s => ({ ...s, countdown: s.countdown <= 1 ? OI_REFRESH_SEC : s.countdown - 1 }))
      , 1000);
    }
    return () => {};
  }, [state.running]);

  const onFetchSpot = async () => {
    set("fetchingSpot", true);
    const spot = await fetchLiveSpot(activeToken.current, activeClientId.current);
    if (spot) set("spotPrice", roundIndexSpotTo100(spot));
    set("fetchingSpot", false);
  };

  const rows      = data ? (view === "calls" ? data.calls : data.puts) : [];
  const pcrColor  = !data ? C.muted : data.pcr < 0.7 ? C.red : data.pcr > 1.5 ? C.green : data.pcr <= 1.0 ? "#f87171" : "#86efac";
  const labelColor = l => l === "ATM" ? C.amber : l === "ITM" ? (view === "calls" ? C.green : C.red) : l === "OTM" ? (view === "calls" ? C.red : C.green) : C.muted;
  const ceOIChange = data ? data.calls.reduce((s, r) => s + (r.oiChange || 0), 0) : 0;
  const peOIChange = data ? data.puts.reduce((s,  r) => s + (r.oiChange || 0), 0) : 0;

  const oiSig = (() => {
    if (!data) return null;
    if (ceOIChange > 0 && peOIChange > 0)
      return ceOIChange > peOIChange
        ? { label: "CALL WRITERS ADDING — BEARISH SHIFT",                    color: C.red,   arrow: "▼" }
        : { label: "PUT WRITERS ADDING — BULLISH SHIFT",                     color: C.green, arrow: "▲" };
    if (ceOIChange > 0 && peOIChange <= 0)
      return { label: "CALL WRITERS ADDING · PUT WRITERS EXITING — BEARISH", color: C.red,   arrow: "▼" };
    if (peOIChange > 0 && ceOIChange <= 0)
      return { label: "PUT WRITERS ADDING · CALL WRITERS EXITING — BULLISH", color: C.green, arrow: "▲" };
    if (ceOIChange < 0 && peOIChange < 0)
      return { label: "BOTH WRITERS EXITING — UNWIND / REVERSAL POSSIBLE",   color: C.amber, arrow: "◆" };
    return { label: "NEUTRAL OI CHANGE", color: C.muted, arrow: "◆" };
  })();

  const zones = (() => {
    if (!data) return { resistance: [], support: [] };
    const resistance = [...data.calls]
      .sort((a, b) => (b.oi || 0) - (a.oi || 0))
      .slice(0, 4)
      .sort((a, b) => a.strike - b.strike);
    const support = [...data.puts]
      .sort((a, b) => (b.oi || 0) - (a.oi || 0))
      .slice(0, 4)
      .sort((a, b) => b.strike - a.strike);
    return { resistance, support };
  })();

  return (
    <div>
      <style>{`
        @keyframes sellerZoneBlink {
          0%, 100% { opacity: 1; box-shadow: none; }
          50% { opacity: .35; box-shadow: 0 0 10px currentColor; }
        }

        .seller-zone-change {
          display: inline-block;
          margin-top: 4px;
          padding: 2px 7px;
          border: 1px solid currentColor;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
          animation: sellerZoneBlink .7s ease-in-out infinite;
        }
      `}</style>

      {/* Controls */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>

          <div>
            <div style={S.label}>BANKEX SPOT PRICE</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input type="number" value={spotPrice} onChange={e => set("spotPrice", e.target.value)}
                disabled={running}
                style={{ background: "#060d18", border: `1px solid ${C.dim}`, color: C.accent, padding: "8px 12px", borderRadius: 4, fontSize: 16, fontFamily: "inherit", width: 130, fontWeight: "bold" }} />
              <button onClick={onFetchSpot} disabled={running || fetchingSpot}
                style={{ background: fetchingSpot ? C.muted : "#0f2040", border: `1px solid ${C.dim}`, color: C.accent, padding: "8px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>
                {fetchingSpot ? "⟳" : "⟲ LIVE"}
              </button>
            </div>
          </div>

          {/* ACCESS TOKEN */}
          <div>
            <div style={S.label}>ACCESS TOKEN <span style={{ color: C.amber }}>*</span></div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {editingToken ? (
                <input type="text" value={tokenInput} onChange={e => setTokenInput(e.target.value)}
                  disabled={running} placeholder="Paste new token..."
                  style={{ background: "#060d18", border: `1px solid ${C.amber}`, color: C.amber, padding: "8px 10px", borderRadius: 4, fontSize: 10, fontFamily: "inherit", width: 220, fontWeight: "bold" }} />
              ) : (
                <div style={{ background: "#060d18", border: `1px solid ${C.dim}`, color: C.muted, padding: "8px 10px", borderRadius: 4, fontSize: 10, width: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {tokenInput.length > 24 ? tokenInput.slice(0, 12) + "..." + tokenInput.slice(-8) : tokenInput}
                </div>
              )}
              <button onClick={() => {
                setEditingToken(e => !e);
                localStorage.setItem("bx_credentials", JSON.stringify({
                  token:    tokenInput.trim() || ACCESS_TOKEN,
                  clientId: clientIdInput,
                }));
              }}
                style={{ background: editingToken ? "#0f2040" : "#0a1628", border: `1px solid ${editingToken ? C.amber : C.dim}`, color: editingToken ? C.amber : C.muted, padding: "8px 9px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>
                {editingToken ? "✓" : "✎"}
              </button>
            </div>
          </div>

          {/* CLIENT ID */}
          <div>
            <div style={S.label}>CLIENT ID <span style={{ color: C.amber }}>*</span></div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {editingClient ? (
                <input type="text" value={clientIdInput} onChange={e => setClientIdInput(e.target.value)}
                  disabled={running} placeholder="Client ID..."
                  style={{ background: "#060d18", border: `1px solid ${C.amber}`, color: C.amber, padding: "8px 10px", borderRadius: 4, fontSize: 13, fontFamily: "inherit", width: 130, fontWeight: "bold" }} />
              ) : (
                <div style={{ background: "#060d18", border: `1px solid ${C.dim}`, color: C.muted, padding: "8px 10px", borderRadius: 4, fontSize: 13, width: 130 }}>
                  {clientIdInput}
                </div>
              )}
              <button onClick={() => setEditingClient(e => !e)} disabled={running}
                style={{ background: editingClient ? "#0f2040" : "#0a1628", border: `1px solid ${editingClient ? C.amber : C.dim}`, color: editingClient ? C.amber : C.muted, padding: "8px 9px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>
                {editingClient ? "✓" : "✎"}
              </button>
            </div>
          </div>

          {!running
            ? <button onClick={start} disabled={loading} style={{ background: "linear-gradient(135deg,#15803d,#16a34a)", border: "none", color: "#fff", padding: "9px 28px", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", letterSpacing: 3, fontWeight: "bold", fontSize: 12, boxShadow: "0 0 12px #16a34a40" }}>▶ START</button>
            : <button onClick={stop} style={{ background: "linear-gradient(135deg,#b91c1c,#dc2626)", border: "none", color: "#fff", padding: "9px 28px", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", letterSpacing: 3, fontWeight: "bold", fontSize: 12, boxShadow: "0 0 12px #dc262640" }}>■ STOP</button>
          }
          {running && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={S.label}>REFRESH IN</div>
              <div style={{ fontSize: 24, color: C.accent, fontWeight: "bold" }}>{countdown}s</div>
            </div>
          )}
          <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            {loading && <span style={{ fontSize: 11, color: C.amber, letterSpacing: 2 }}>⟳ FETCHING DATA...</span>}
            {error   && <span style={{ fontSize: 11, color: C.red }}>⚠ {error}</span>}
            {lastUpdated && !loading && <span style={{ fontSize: 10, color: C.muted }}>UPDATED {fmtTime(lastUpdated)}</span>}
            {running && !loading && !error && <span style={{ fontSize: 10, color: C.green }}>● LIVE — AUTO REFRESH {OI_REFRESH_SEC}s</span>}
          </div>
        </div>
      </div>

      {/* PCR Box */}
      {data && (
        <div style={{ ...S.card, borderColor: pcrColor + "40", padding: "20px", marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 24, alignItems: "center" }}>
            <div>
              <div style={S.label}>PCR RATIO</div>
              <div style={{ fontSize: 48, color: pcrColor, fontWeight: "bold", lineHeight: 1 }}>{data.pcr}</div>
              <div style={{ fontSize: 9, color: C.muted, marginTop: 4, letterSpacing: 1 }}>PE OI ÷ CE OI</div>
            </div>
            <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 24 }}>
              <div style={S.label}>SIGNAL</div>
              <div style={{ fontSize: 18, color: pcrColor, fontWeight: "bold", letterSpacing: 2, marginBottom: 6 }}>{data.pcrSignal}</div>
              <div style={{ fontSize: 11, color: C.text, lineHeight: 1.6 }}>{data.pcrDesc}</div>
            </div>
            <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 24, display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={S.label}>CALL SELLERS (TOTAL CE OI)</div>
                <div style={{ fontSize: 18, color: C.red, fontWeight: "bold" }}>{fmtOI(data.totalCeOI)}</div>
              </div>
              <div>
                <div style={S.label}>PUT SELLERS (TOTAL PE OI)</div>
                <div style={{ fontSize: 18, color: C.green, fontWeight: "bold" }}>{fmtOI(data.totalPeOI)}</div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", ...S.label, marginBottom: 6 }}>
              <span style={{ color: C.red }}>← BEARISH (PCR &lt; 0.7)</span>
              <span>NEUTRAL (0.7 – 1.5)</span>
              <span style={{ color: C.green }}>(PCR &gt; 1.5) BULLISH →</span>
            </div>
            <div style={{ position: "relative", height: 12, background: `linear-gradient(to right, ${C.red}, ${C.amber} 40%, ${C.green})`, borderRadius: 6 }}>
              <div style={{ position: "absolute", left: `${Math.min(Math.max((data.pcr / 2) * 100, 1), 99)}%`, top: -4, transform: "translateX(-50%)", width: 5, height: 20, background: "#fff", borderRadius: 3, boxShadow: "0 0 8px rgba(255,255,255,0.8)" }} />
            </div>
          </div>
        </div>
      )}

      {/* OI Change Signal */}
      {oiSig && (
        <div style={{ ...S.card, borderColor: oiSig.color + "40", padding: "16px 20px", marginBottom: 14 }}>
          <div style={S.label}>OI CHANGE DIRECTION — REAL-TIME SHIFT</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 24, color: oiSig.color }}>{oiSig.arrow}</span>
            <span style={{ fontSize: 13, color: oiSig.color, fontWeight: "bold", letterSpacing: 2 }}>{oiSig.label}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { label: "CE OI CHANGE (CALL WRITERS)", val: ceOIChange, addText: "Resistance building ▲", exitText: "Resistance weakening ▼", col: v => v > 0 ? C.red : C.green },
              { label: "PE OI CHANGE (PUT WRITERS)",  val: peOIChange, addText: "Support building ▲",    exitText: "Support weakening ▼",    col: v => v > 0 ? C.green : C.red },
            ].map(r => (
              <div key={r.label} style={{ background: "#060d18", borderRadius: 6, padding: "12px 16px" }}>
                <div style={S.label}>{r.label}</div>
                <div style={{ fontSize: 20, color: r.col(r.val), fontWeight: "bold", marginBottom: 4 }}>
                  {r.val >= 0 ? `▲ +${fmtOI(r.val)}` : `▼ ${fmtOI(r.val)}`}
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>{r.val > 0 ? r.addText : r.exitText}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Writer Strength */}
      {data && (() => {
        const ceW = data.totalCeOI, peW = data.totalPeOI, total = ceW + peW;
        const ceP = total > 0 ? ((ceW / total) * 100).toFixed(1) : 50;
        const peP = total > 0 ? ((peW / total) * 100).toFixed(1) : 50;
        const s = ceW > peW
          ? { label: "CALL SELLERS STRONGER", sub: "Resistance building — bearish pressure", color: C.red,   arrow: "▼" }
          : peW > ceW
          ? { label: "PUT SELLERS STRONGER",  sub: "Support building — bullish pressure",    color: C.green, arrow: "▲" }
          : { label: "EQUAL WRITER STRENGTH", sub: "Balanced — no directional bias",          color: C.amber, arrow: "◆" };
        return (
          <div style={{ ...S.card, borderColor: s.color + "40", padding: "16px 20px", marginBottom: 14 }}>
            <div style={S.label}>COMBINED WRITER STRENGTH — BANKEX</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22, color: s.color }}>{s.arrow}</span>
              <div>
                <div style={{ fontSize: 14, color: s.color, fontWeight: "bold", letterSpacing: 2 }}>{s.label}</div>
                <div style={{ fontSize: 11, color: C.text, marginTop: 2 }}>{s.sub}</div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", ...S.label, marginBottom: 4 }}>
              <span style={{ color: C.red }}>CALL WRITERS {fmtOI(ceW)} ({ceP}%)</span>
              <span style={{ color: C.green }}>PUT WRITERS {fmtOI(peW)} ({peP}%)</span>
            </div>
            <div style={{ height: 10, background: C.border, borderRadius: 5, overflow: "hidden", display: "flex" }}>
              <div style={{ width: `${ceP}%`, background: `linear-gradient(90deg,#b91c1c,${C.red})`, transition: "width 0.8s ease" }} />
              <div style={{ flex: 1, background: `linear-gradient(90deg,#15803d,${C.green})` }} />
            </div>
          </div>
        );
      })()}

        {/* Resistance & Support Zones */}
      {data && zones.resistance.length > 0 && zones.support.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
          <div style={{ ...S.card, borderColor: C.red + "40", overflow: "hidden" }}>
            <div style={{ background: "#060d18", padding: "10px 16px", fontSize: 10, color: C.red, letterSpacing: 3, fontWeight: "bold", borderBottom: `1px solid ${C.border}` }}>
              🔴 TOP 4 RESISTANCE ZONES — BANKEX
            </div>
            <div style={{ padding: "4px 0" }}>
              {zones.resistance.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: i < zones.resistance.length - 1 ? `1px solid #060d18` : "none", background: i === 0 ? "#0d1220" : C.card }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${C.red}20`, border: `1px solid ${C.red}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: C.red, fontWeight: "bold" }}>R{i + 1}</div>
                    <div>
                      <div style={{ fontSize: 20, color: C.red, fontWeight: "bold", letterSpacing: 1 }}>{r.strike}</div>
                      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginTop: 2 }}>STRIKE LEVEL</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, color: C.text, fontWeight: "bold" }}>{fmtOI(r.oi)}</div>
                    <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2 }}>OPEN INTEREST</div>
                    <div
                      key={`res-${r.strike}-${r.oiChange}`}
                      className={Number(r.oiChange || 0) !== 0 ? "seller-zone-change" : ""}
                      style={{ color: Number(r.oiChange || 0) > 0 ? C.red : Number(r.oiChange || 0) < 0 ? C.green : C.muted }}
                    >
                      {Number(r.oiChange || 0) > 0
                        ? `[▲ +${fmtOI(Math.abs(r.oiChange))} ADDED]`
                        : Number(r.oiChange || 0) < 0
                          ? `[▼ ${fmtOI(Math.abs(r.oiChange))} LEFT]`
                          : "[• 0 CHANGE]"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: "8px 16px", background: `${C.red}08`, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 9, color: C.red, letterSpacing: 2 }}>▼ CALL WRITERS STRONG HERE — PRICE MAY FACE SELLING PRESSURE</div>
            </div>
          </div>

          <div style={{ ...S.card, borderColor: C.green + "40", overflow: "hidden" }}>
            <div style={{ background: "#060d18", padding: "10px 16px", fontSize: 10, color: C.green, letterSpacing: 3, fontWeight: "bold", borderBottom: `1px solid ${C.border}` }}>
              🟢 TOP 4 SUPPORT ZONES — BANKEX
            </div>
            <div style={{ padding: "4px 0" }}>
              {zones.support.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: i < zones.support.length - 1 ? `1px solid #060d18` : "none", background: i === 0 ? "#0a1a0e" : C.card }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${C.green}20`, border: `1px solid ${C.green}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: C.green, fontWeight: "bold" }}>S{i + 1}</div>
                    <div>
                      <div style={{ fontSize: 20, color: C.green, fontWeight: "bold", letterSpacing: 1 }}>{r.strike}</div>
                      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginTop: 2 }}>STRIKE LEVEL</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, color: C.text, fontWeight: "bold" }}>{fmtOI(r.oi)}</div>
                    <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2 }}>OPEN INTEREST</div>
                    <div
                      key={`sup-${r.strike}-${r.oiChange}`}
                      className={Number(r.oiChange || 0) !== 0 ? "seller-zone-change" : ""}
                      style={{ color: Number(r.oiChange || 0) > 0 ? C.green : Number(r.oiChange || 0) < 0 ? C.red : C.muted }}
                    >
                      {Number(r.oiChange || 0) > 0
                        ? `[▲ +${fmtOI(Math.abs(r.oiChange))} ADDED]`
                        : Number(r.oiChange || 0) < 0
                          ? `[▼ ${fmtOI(Math.abs(r.oiChange))} LEFT]`
                          : "[• 0 CHANGE]"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: "8px 16px", background: `${C.green}08`, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 9, color: C.green, letterSpacing: 2 }}>▲ PUT WRITERS STRONG HERE — PRICE MAY FIND BUYING SUPPORT</div>
            </div>
          </div>
        </div>
      )}

      {/* View Toggle */}
      {data && (
        <div style={{ display: "flex", marginBottom: 14, borderRadius: 6, overflow: "hidden", border: `1px solid ${C.border}`, width: "fit-content" }}>
          {[
            { id: "calls", label: "📞 CALL SELLERS (CE)", color: C.red   },
            { id: "puts",  label: "📉 PUT SELLERS (PE)",   color: C.green },
          ].map(tab => (
            <button key={tab.id} onClick={() => set("view", tab.id)} style={{
              padding: "8px 24px", fontSize: 11, letterSpacing: 2, fontFamily: "inherit", cursor: "pointer", border: "none",
              background: view === tab.id ? "#060d18" : C.card,
              color: view === tab.id ? tab.color : C.muted,
              borderBottom: view === tab.id ? `2px solid ${tab.color}` : "2px solid transparent",
              fontWeight: view === tab.id ? "bold" : "normal",
            }}>{tab.label}</button>
          ))}
        </div>
      )}

      {/* OI Table */}
      {data && (
        <div style={{ ...S.card, overflow: "hidden" }}>
          <div style={{ background: "#060d18", padding: "10px 16px", fontSize: 10, color: view === "calls" ? C.red : C.green, letterSpacing: 3, fontWeight: "bold", borderBottom: `1px solid ${C.border}` }}>
            {view === "calls" ? "📞 CALL SELLERS — BANKEX CE OI (1 ITM · ATM · 10 OTM)" : "📉 PUT SELLERS — BANKEX PE OI (10 OTM · ATM · 1 ITM)"}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>{["TYPE", "STRIKE", "LTP", "OI", "OI CHANGE", "VOLUME (L)"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ background: r.label === "ATM" ? "#0d1e35" : i % 2 === 0 ? "#070e1a" : C.card }}>
                  <td style={{ ...S.td, textAlign: "left", paddingLeft: 16 }}>
                    <span style={{ fontSize: 9, fontWeight: "bold", letterSpacing: 1, color: labelColor(r.label), border: `1px solid ${labelColor(r.label)}30`, padding: "2px 7px", borderRadius: 3 }}>{r.label}</span>
                  </td>
                  <td style={{ ...S.td, color: r.label === "ATM" ? C.amber : C.text, fontWeight: r.label === "ATM" ? "bold" : "normal" }}>{r.strike}</td>
                  <td style={{ ...S.td, color: C.muted }}>{fmtLTP(r.ltp)}</td>
                  <td style={{ ...S.td, color: view === "calls" ? C.red : C.green, fontWeight: "bold" }}>{fmtOI(r.oi)}</td>
                  <td style={{ ...S.td, color: (r.oiChange || 0) > 0 ? C.green : (r.oiChange || 0) < 0 ? C.red : C.muted }}>
                    {(r.oiChange || 0) > 0 ? "▲ " : (r.oiChange || 0) < 0 ? "▼ " : ""}{fmtOI(Math.abs(r.oiChange || 0))}
                  </td>
                  <td style={{ ...S.td, color: C.text }}>{fmtVolCr(r.volume)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!data && !loading && (
        <div style={{ textAlign: "center", padding: "100px 0", color: C.dim }}>
          <div style={{ fontSize: 56, marginBottom: 16, color: C.accent, opacity: 0.3 }}>⬡</div>
          <div style={{ fontSize: 11, letterSpacing: 4, color: C.muted }}>PRESS ▶ START TO BEGIN OI ANALYSIS</div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN BANKEX PAGE
// ════════════════════════════════════════════════════════════════
export default function BankexPage({ state: parentState, setState: setParentState, refreshSec = REFRESH_SEC } = {}) {
  const navigate = useNavigate();
  const liveRefreshSec = Math.max(5, Number(refreshSec || REFRESH_SEC));

  const [activePage, setActivePage] = useState("itm");
  const [oiState, setOiState] = useState({
    spotPrice: "55000", running: false, loading: false,
    data: null, error: "", countdown: OI_REFRESH_SEC,
    view: "calls", lastUpdated: null, fetchingSpot: false,
    accessToken: parentState?.accessToken || "",
    clientId: parentState?.clientId || "",
  });

  const [spotPrice,     setSpotPrice]    = useState("62000");
  const [running,       setRunning]      = useState(!!parentState?.running);
  const [loading,       setLoading]      = useState(false);
  const [fetchingSpot,  setFetchingSpot] = useState(false);
  const [data,          setData]         = useState(null);
  const [error,         setError]        = useState("");
  const [countdown,     setCountdown]    = useState(liveRefreshSec);
  const [lastUpdated,   setLastUpdated]  = useState(null);
  const [tokenInput,    setTokenInput]   = useState(ACCESS_TOKEN);
  const [clientIdInput, setClientIdInput] = useState(CLIENT_ID);
  const [editingToken,  setEditingToken] = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const activeToken    = useRef(ACCESS_TOKEN);
  const activeClientId = useRef(CLIENT_ID);

  useEffect(() => {
    const nextToken = parentState?.accessToken || "";
    const nextClientId = parentState?.clientId || "";
    const nextSpot = parentState?.spotPrice || "";

    if (!nextToken && !nextClientId && !nextSpot) return;

    if (nextToken) {
      setTokenInput(nextToken);
      activeToken.current = nextToken;
    }

    if (nextClientId) {
      setClientIdInput(nextClientId);
      activeClientId.current = nextClientId;
    }

    if (nextSpot) {
      setSpotPrice(nextSpot);
    }

    setOiState(s => ({
      ...s,
      accessToken: nextToken || s.accessToken,
      clientId: nextClientId || s.clientId,
      spotPrice: nextSpot || s.spotPrice,
    }));

    localStorage.setItem("bx_credentials", JSON.stringify({
      token: nextToken,
      clientId: nextClientId,
    }));
  }, [parentState?.accessToken, parentState?.clientId, parentState?.spotPrice]);

  useEffect(() => {
  const saved = JSON.parse(localStorage.getItem("bx_credentials") || "{}");
  if (saved.token)    { setTokenInput(saved.token);       activeToken.current    = saved.token;    }
  if (saved.clientId) { setClientIdInput(saved.clientId); activeClientId.current = saved.clientId; }
}, []);

useEffect(() => {
  if (tokenInput !== ACCESS_TOKEN || clientIdInput !== CLIENT_ID) {
    localStorage.setItem("bx_credentials", JSON.stringify({ token: tokenInput, clientId: clientIdInput }));
  }
}, [tokenInput, clientIdInput]);

  const ivRef = useRef(null);
  const cdRef = useRef(null);

  const fetchChain = useCallback(async () => {
    setLoading(true);
    try {
      const expData = await safeFetch(`${API_BASE}/api/options/expiry`, {
        accessToken: activeToken.current,
        clientId:    activeClientId.current,
        underlyingId: UNDERLYING_ID,
        segment:      SEGMENT,
      });
      if (!expData?.expiry) throw new Error("No expiry found");

      const d = await safeFetch(`${API_BASE}/api/options/bankex-chain`, {
        accessToken:  activeToken.current,
        clientId:     activeClientId.current,
        expiry:       expData.expiry,
        spotPrice:    parseFloat(spotPrice),
        underlyingId: UNDERLYING_ID,
        segment:      SEGMENT,
        range:        RANGE,
      });
      if (d?.error) throw new Error(d.error);
      setData(d);
      setError("");
      setLastUpdated(new Date().toISOString());
      setCountdown(liveRefreshSec);
    } catch (e) {
      setError(e.message?.slice(0, 60));
    } finally {
      setLoading(false);
    }
  }, [spotPrice, liveRefreshSec]);

  const start = async () => {
    activeToken.current    = tokenInput.trim()    || ACCESS_TOKEN;
    activeClientId.current = clientIdInput.trim() || CLIENT_ID;
    setRunning(true);
    setParentState?.(s => ({
      ...s,
      running: true,
      countdown: liveRefreshSec,
      spotPrice,
      clientId: activeClientId.current,
      accessToken: activeToken.current,
    }));
    setError("");
    await fetchChain();
    if (ivRef.current) clearInterval(ivRef.current);
    if (cdRef.current) clearInterval(cdRef.current);
    ivRef.current = setInterval(fetchChain, liveRefreshSec * 1000);
    cdRef.current = setInterval(() =>
      setCountdown(c => c <= 1 ? liveRefreshSec : c - 1)
    , 1000);
  };

  const stop = () => {
    clearInterval(ivRef.current); clearInterval(cdRef.current);
    ivRef.current = null; cdRef.current = null;
    setRunning(false);
    setCountdown(liveRefreshSec);
    setParentState?.(s => ({
      ...s,
      running: false,
      countdown: liveRefreshSec,
    }));
  };

  useEffect(() => {
    if (running && !ivRef.current) {
      ivRef.current = setInterval(fetchChain, liveRefreshSec * 1000);
      cdRef.current = setInterval(() =>
        setCountdown(c => c <= 1 ? liveRefreshSec : c - 1)
      , 1000);
    }
    return () => {};
  }, [running, liveRefreshSec, fetchChain]);

  const onFetchSpot = async () => {
    setFetchingSpot(true);
    const spot = await fetchLiveSpot(activeToken.current, activeClientId.current);
    if (spot) {
      const roundedSpot = roundIndexSpotTo100(spot);
      setSpotPrice(roundedSpot);
      if (setParentState) {
        setParentState(s => ({ ...s, spotPrice: roundedSpot }));
      }
    }
    setFetchingSpot(false);
  };

  const logout = () => {
    localStorage.removeItem("ct_login_token");
    localStorage.removeItem("ct_login_user");
    localStorage.removeItem("ct_admin_auth");
    navigate("/login", { replace: true });
  };

  const getSentiment = (s) => {
    if (!s) return null;
    const v = s.ceVolumeCr > s.peVolumeCr ? 1 : s.ceVolumeCr < s.peVolumeCr ? -1 : 0;
    const o = s.ceOI > s.peOI ? 1 : s.ceOI < s.peOI ? -1 : 0;
    const t = v + o;
    if (t >= 1)  return { label: "CALL BUYERS & PUT SELLERS DOMINANT — BULLISH", color: C.green, arrow: "▲" };
    if (t <= -1) return { label: "PUT BUYERS & CALL SELLERS DOMINANT — BEARISH",  color: C.red,   arrow: "▼" };
    return { label: "BALANCED — NO CLEAR DOMINANCE", color: C.amber, arrow: "◆" };
  };
  const sentiment = data ? getSentiment(data.summary) : null;

  return (
    <div style={S.page}>

      {/* TOP BAR */}
      <div style={{
        background: "linear-gradient(180deg, #080f1a, #04090f)",
        borderBottom: `1px solid ${C.border}`,
        padding: "0 24px",
        display: "flex", justifyContent: "space-between", alignItems: "stretch",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, padding: "14px 0" }}>
          <div>
            <div style={{ fontSize: 15, color: C.accent, fontWeight: "bold", letterSpacing: 4 }}>⬡ OC TERMINAL</div>
            <div style={{ fontSize: 8, color: C.muted, letterSpacing: 3 }}>BANKEX SCANNER</div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "stretch" }}>
          {PAGES.map(p => {
            const isActive  = activePage === p.id;
            const isRunning = p.id === "itm" ? running : oiState.running;
            return (
              <button key={p.id} onClick={() => setActivePage(p.id)} style={{
                padding: "0 24px", fontSize: 11, letterSpacing: 2, fontFamily: "inherit",
                cursor: "pointer", border: "none",
                borderBottom: isActive ? `2px solid ${p.dot}` : "2px solid transparent",
                background: "transparent",
                color: isActive ? p.dot : C.muted,
                fontWeight: isActive ? "bold" : "normal",
                display: "flex", alignItems: "center", gap: 7,
              }}>
                {isRunning && <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, boxShadow: `0 0 6px ${C.green}` }} />}
                {p.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 0" }}>
          {running && <span style={{ fontSize: 10, color: C.green, letterSpacing: 2 }}>● LIVE — {countdown}s</span>}
          <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, textAlign: "right" }}>
            <div>REFRESH RATE</div>
            <div style={{ color: C.accent, fontSize: 12 }}>{liveRefreshSec}s</div>
          </div>
          <button onClick={logout} style={{
            background: "transparent", border: `1px solid ${C.red}40`, color: C.red,
            padding: "6px 14px", borderRadius: 4, cursor: "pointer", fontSize: 10, letterSpacing: 2, fontFamily: "inherit",
          }}>LOGOUT</button>
        </div>
      </div>

      <div style={{ padding: "20px 24px", maxWidth: 1300, margin: "0 auto" }}>

        {/* ── ITM TAB ── */}
        <div style={{ display: activePage === "itm" ? "block" : "none" }}>

          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>

              {/* SPOT PRICE */}
              <div>
                <div style={S.label}>BANKEX SPOT PRICE</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="number" value={spotPrice} onChange={e => setSpotPrice(e.target.value)}
                    disabled={running}
                    style={{ background: "#060d18", border: `1px solid ${C.dim}`, color: C.accent, padding: "8px 12px", borderRadius: 4, fontSize: 16, fontFamily: "inherit", width: 130, fontWeight: "bold" }} />
                  <button onClick={onFetchSpot} disabled={running || fetchingSpot}
                    style={{ background: fetchingSpot ? C.muted : "#0f2040", border: `1px solid ${C.dim}`, color: C.accent, padding: "8px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>
                    {fetchingSpot ? "⟳" : "⟲ LIVE"}
                  </button>
                </div>
              </div>

              {/* ACCESS TOKEN */}
              <div>
                <div style={S.label}>ACCESS TOKEN</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {editingToken ? (
                    <input type="text" value={tokenInput} onChange={e => setTokenInput(e.target.value)}
                      disabled={running}
                      style={{ background: "#060d18", border: `1px solid ${C.amber}`, color: C.amber, padding: "8px 10px", borderRadius: 4, fontSize: 10, width: 220, fontFamily: "inherit" }} />
                  ) : (
                    <div style={{ background: "#060d18", border: `1px solid ${C.dim}`, color: C.muted, padding: "8px 10px", borderRadius: 4, fontSize: 10, width: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {tokenInput.slice(0, 12) + "..." + tokenInput.slice(-8)}
                    </div>
                  )}
                  <button onClick={() => {
  setEditingToken(e => !e);
  localStorage.setItem("bx_credentials", JSON.stringify({
    token:    tokenInput.trim() || ACCESS_TOKEN,
    clientId: clientIdInput,
  }));
}}
  style={{ background: editingToken ? "#0f2040" : "#0a1628", border: `1px solid ${editingToken ? C.amber : C.dim}`, color: editingToken ? C.amber : C.muted, padding: "8px 9px", borderRadius: 4, cursor: "pointer" }}>
  {editingToken ? "✓" : "✎"}
</button>
                </div>
              </div>

              {/* CLIENT ID */}
              <div>
                <div style={S.label}>CLIENT ID</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {editingClient ? (
                    <input type="text" value={clientIdInput} onChange={e => setClientIdInput(e.target.value)}
                      disabled={running}
                      style={{ background: "#060d18", border: `1px solid ${C.amber}`, color: C.amber, padding: "8px 10px", borderRadius: 4, fontSize: 13, width: 130, fontFamily: "inherit" }} />
                  ) : (
                    <div style={{ background: "#060d18", border: `1px solid ${C.dim}`, color: C.muted, padding: "8px 10px", borderRadius: 4, fontSize: 13, width: 130 }}>
                      {clientIdInput}
                    </div>
                  )}
                  <button onClick={() => setEditingClient(e => !e)} disabled={running}
                    style={{ background: editingClient ? "#0f2040" : "#0a1628", border: `1px solid ${editingClient ? C.amber : C.dim}`, color: editingClient ? C.amber : C.muted, padding: "8px 9px", borderRadius: 4, cursor: "pointer" }}>
                    {editingClient ? "✓" : "✎"}
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {!running
                  ? <button onClick={start} disabled={loading} style={{ background: "linear-gradient(135deg,#15803d,#16a34a)", border: "none", color: "#fff", padding: "9px 28px", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", letterSpacing: 3, fontWeight: "bold", fontSize: 12, boxShadow: "0 0 12px #16a34a40" }}>▶ START</button>
                  : <button onClick={stop} style={{ background: "linear-gradient(135deg,#b91c1c,#dc2626)", border: "none", color: "#fff", padding: "9px 28px", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", letterSpacing: 3, fontWeight: "bold", fontSize: 12, boxShadow: "0 0 12px #dc262640" }}>■ STOP</button>
                }
              </div>
              {running && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 60 }}>
                  <div style={S.label}>REFRESH IN</div>
                  <div style={{ fontSize: 24, color: C.accent, fontWeight: "bold", lineHeight: 1 }}>{countdown}s</div>
                </div>
              )}
              <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                {loading    && <span style={{ fontSize: 11, color: C.amber, letterSpacing: 2 }}>⟳ FETCHING DATA...</span>}
                {error      && <span style={{ fontSize: 11, color: C.red }}>⚠ {error}</span>}
                {lastUpdated && !loading && <span style={{ fontSize: 10, color: C.muted }}>UPDATED {fmtTime(lastUpdated)}</span>}
                {running && !loading && !error && <span style={{ fontSize: 10, color: C.green }}>● LIVE — AUTO REFRESH {REFRESH_SEC}s</span>}
              </div>
            </div>
          </div>

          {sentiment && (
            <div style={{ ...S.card, borderColor: sentiment.color + "30", padding: "16px 20px", marginBottom: 16 }}>
              <div style={S.label}>MARKET SENTIMENT — BANKEX ITM ANALYSIS</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 24, color: sentiment.color }}>{sentiment.arrow}</span>
                <span style={{ fontSize: 14, color: sentiment.color, fontWeight: "bold", letterSpacing: 2 }}>{sentiment.label}</span>
              </div>
              <SentimentBar ceVal={data.summary.ceVolumeCr} peVal={data.summary.peVolumeCr}
                ceLabel={`CE VOL ${data.summary.ceVolumeCr} L`} peLabel={`PE VOL ${data.summary.peVolumeCr} L`} title="VOLUME" />
              <SentimentBar ceVal={data.summary.ceOI} peVal={data.summary.peOI}
                ceLabel={`CE OI ${fmtOI(data.summary.ceOI)}`} peLabel={`PE OI ${fmtOI(data.summary.peOI)}`} title="OPEN INTEREST" />
            </div>
          )}

          {sentiment && (
            <ParentOrderAlignmentPanel
              title="BANKEX ITM PARENT ORDER ALIGNMENT"
              marketSide={sentiment.label.includes("BULLISH") ? "BUY" : sentiment.label.includes("BEARISH") ? "SELL" : ""}
            />
          )}

          {data && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
              {[
                { label: "CE VOLUME",        value: `${data.summary.ceVolumeCr} L`, color: C.green },
                { label: "PE VOLUME",        value: `${data.summary.peVolumeCr} L`, color: C.red   },
                { label: "CE OPEN INTEREST", value: fmtOI(data.summary.ceOI),       color: C.green },
                { label: "PE OPEN INTEREST", value: fmtOI(data.summary.peOI),       color: C.red   },
              ].map(c => (
                <div key={c.label} style={{ ...S.card, padding: "14px 16px" }}>
                  <div style={S.label}>{c.label}</div>
                  <div style={{ fontSize: 18, color: c.color, fontWeight: "bold", marginTop: 4 }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          {data && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              {[
                { title: "▲ TOP 10 ITM CALLS — BANKEX CE", rows: data.calls, color: C.green },
                { title: "▼ TOP 10 ITM PUTS — BANKEX PE",  rows: data.puts,  color: C.red   },
              ].map(t => (
                <div key={t.title} style={{ ...S.card, overflow: "hidden" }}>
                  <div style={{ background: "#060d18", padding: "10px 16px", fontSize: 10, color: t.color, letterSpacing: 3, fontWeight: "bold", borderBottom: `1px solid ${C.border}` }}>{t.title}</div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>{["STRIKE", "LTP", "VOLUME (L)", "OI"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {t.rows.map((r, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#070e1a" : C.card }}>
                          <td style={{ ...S.td, color: t.color, fontWeight: "bold" }}>{r.strikePrice}</td>
                          <td style={{ ...S.td, color: C.muted }}>{fmtLTP(r.ltp)}</td>
                          <td style={{ ...S.td, color: C.text }}>{fmtVolCr(r.volume)}</td>
                          <td style={{ ...S.td, color: C.text }}>{fmtOI(r.oi)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}

          {data && data.otmCalls && data.otmPuts && (
            <>
              <div style={{ fontSize: 10, color: C.muted, letterSpacing: 3, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>OTM OVERVIEW — NEAREST 2 STRIKES</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                {[
                  { title: "🔺 2 OTM CALLS — BANKEX CE", rows: data.otmCalls, color: C.amber    },
                  { title: "🔻 2 OTM PUTS — BANKEX PE",  rows: data.otmPuts,  color: "#a78bfa"  },
                ].map(t => (
                  <div key={t.title} style={{ ...S.card, overflow: "hidden" }}>
                    <div style={{ background: "#060d18", padding: "10px 16px", fontSize: 10, color: t.color, letterSpacing: 3, fontWeight: "bold", borderBottom: `1px solid ${C.border}` }}>{t.title}</div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>{["STRIKE", "LTP", "VOLUME (L)", "OI"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {t.rows.map((r, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#070e1a" : C.card }}>
                            <td style={{ ...S.td, color: t.color, fontWeight: "bold" }}>{r.strikePrice}</td>
                            <td style={{ ...S.td, color: C.muted }}>{fmtLTP(r.ltp)}</td>
                            <td style={{ ...S.td, color: C.text }}>{fmtVolCr(r.volume)}</td>
                            <td style={{ ...S.td, color: C.text }}>{fmtOI(r.oi)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </>
          )}

          {data && data.otmCalls && data.otmPuts && (
            <div style={{ ...S.card, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ background: "#060d18", padding: "10px 16px", fontSize: 10, color: C.accent, letterSpacing: 3, fontWeight: "bold", borderBottom: `1px solid ${C.border}` }}>📊 SUMMARY TABLE</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["TYPE", "TOTAL VOLUME (L)", "TOTAL OI"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {[
                    { type: "ITM CE", vol: data.summary.ceVolumeCr, oi: data.summary.ceOI, color: C.green   },
                    { type: "ITM PE", vol: data.summary.peVolumeCr, oi: data.summary.peOI, color: C.red     },
                    { type: "OTM CE", vol: (data.otmCalls.reduce((s,r) => s+(r.volume||0),0)/1e5).toFixed(2), oi: data.otmCalls.reduce((s,r) => s+(r.oi||0),0), color: C.amber   },
                    { type: "OTM PE", vol: (data.otmPuts.reduce((s,r)  => s+(r.volume||0),0)/1e5).toFixed(2), oi: data.otmPuts.reduce((s,r)  => s+(r.oi||0),0), color: "#a78bfa" },
                  ].map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#070e1a" : C.card }}>
                      <td style={{ ...S.td, textAlign: "left", paddingLeft: 16, color: r.color, fontWeight: "bold" }}>{r.type}</td>
                      <td style={{ ...S.td, color: C.text }}>{r.vol}</td>
                      <td style={{ ...S.td, color: C.text }}>{fmtOI(r.oi)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!data && !loading && (
            <div style={{ textAlign: "center", padding: "100px 0", color: C.dim }}>
              <div style={{ fontSize: 56, marginBottom: 16, color: C.accent, opacity: 0.3 }}>⬡</div>
              <div style={{ fontSize: 11, letterSpacing: 4, color: C.muted }}>PRESS ▶ START TO BEGIN LIVE SCAN</div>
              <div style={{ fontSize: 10, letterSpacing: 2, color: C.dim, marginTop: 8 }}>AUTO REFRESHES EVERY {REFRESH_SEC} SECONDS</div>
            </div>
          )}
        </div>

        {/* ── OI ANALYSIS TAB ── */}
        <div style={{ display: activePage === "bankex-oi" ? "block" : "none" }}>
          <BankexOIAnalysis state={oiState} setState={setOiState} />
        </div>

      </div>
    </div>
  );
}